import React, { useEffect, useState } from "react";
// Libraries
import { useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
// Utils
import { GetSupportedSystems_integrationSystems } from "api/Import/OtherSystems/types/GetSupportedSystems";
import {
  BCMS_IMPORT_CSV,
  FARMPLAN_IMPORT_XLS,
  TRU_TEST_IMPORT_XLS,
  APHIS_IMPORT_CSV,
} from "api/Import/OtherSystems/mutations";
import { BcmsImportCsv, BcmsImportCsvVariables } from "api/Import/OtherSystems/types/BcmsImportCsv";
import { TruTestImportXls, TruTestImportXlsVariables } from "api/Import/OtherSystems/types/TruTestImportXls";
import { FarmPlanImportXls, FarmPlanImportXlsVariables } from "api/Import/OtherSystems/types/FarmPlanImportXls";
import { AphisImportCsv, AphisImportCsvVariables } from "api/Import/OtherSystems/types/AphisImportCsv";
import { ELEMENTS_IDS } from "constants/ElementsIds";
import { BCMS_SLUG, FARMPLAN_SLUG, TRU_TEST_SLUG, APHIS_SLUG, AA_SLUG, NEOGEN_SLUG } from "constants/Import";
import { IMPORT } from "constants/Routes";
import { XLS_FILE_FORMAT, CSV_FILE_FORMAT } from "constants/Settings";
import { getBUFromLocalStorage, setTaskIdToLocalStorage } from "helpers/storage";
import { useEventRequest, useGetSupportedSystems, useHasFeature } from "hooks";
import { SystemSelect } from "components/Import";
import { Button, FileInput, Flex, Spacer, Text, TextAnchor } from "components";
import styles from "./ImportWeightForm.module.scss";
import LogoAngus from "assets/img/Epds/logo-Angus.png";
import LogoNeogen from "assets/img/Epds/logo-Neogen.png";
import { useGetBatchUploadFormatLazyQuery, useUploadCsvMutation, useImportCsvMutation } from "generated/graphql";
import { AsyncJobStatusMenuImport } from "animals/AsyncJobStatusMenuImport";
import { setImportMenuState, setImportTaskId } from "state/asyncImportJobSlice";
import { useDispatch } from "react-redux";
import { handleMatchColumns } from "hooks/useHandleMatchColumns";

export interface ImportWeightFile {
  fileName: string;
  file: File;
}

interface ImportWeightFormProps {
  error: string;
  file: ImportWeightFile | null;
  handleRemoveFile: () => void;
  handleSetFile: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setError: (error: string) => void;
  setTaskId: (id: string) => void;
  setSelectedSystem: (system: GetSupportedSystems_integrationSystems) => void;
}

export const ImportWeightForm: React.FC<ImportWeightFormProps> = ({
  error,
  file,
  handleRemoveFile,
  handleSetFile,
  setError,
  setTaskId,
  setSelectedSystem: parentSetSelectedSystem,
}) => {
  const dispatch = useDispatch();
  const [getBatchUploadFormat] = useGetBatchUploadFormatLazyQuery();
  const { eventsRequest } = useEventRequest();
  const [importCsvMutation] = useImportCsvMutation();

  const useEpds = useHasFeature("EPDS");
  let integrationSystems;
  const { data } = useGetSupportedSystems();

  if (useEpds) {
    integrationSystems = [
      {
        __typename: "IntegrationSystemType",
        id: "1",
        name: "American Angus",
        logo: LogoAngus,
        helpLink: null,
        fields: [],
        slug: "cmd-aa-epd",
      },
      {
        __typename: "IntegrationSystemType",
        id: "2",
        name: "Neogen",
        logo: LogoNeogen,
        helpLink: null,
        fields: [],
        slug: "cmd-neogen-epd",
      },
    ];
  } else {
    integrationSystems = data;
  }

  const [selectedSystem, setSelectedSystem] = useState<GetSupportedSystems_integrationSystems | null>(
    integrationSystems?.[0] || null,
  );

  useEffect(() => {
    handleRemoveFile();
    if (selectedSystem) parentSetSelectedSystem(selectedSystem);
  }, [selectedSystem]);

  const [uploadBcmsFile] = useMutation<BcmsImportCsv, BcmsImportCsvVariables>(BCMS_IMPORT_CSV);
  const [uploadTruTestFile] = useMutation<TruTestImportXls, TruTestImportXlsVariables>(TRU_TEST_IMPORT_XLS);
  const [uploadFarmPlanFile] = useMutation<FarmPlanImportXls, FarmPlanImportXlsVariables>(FARMPLAN_IMPORT_XLS);
  const [uploadAphisFile] = useMutation<AphisImportCsv, AphisImportCsvVariables>(APHIS_IMPORT_CSV);
  const [uploadCsvMutation] = useUploadCsvMutation();

  const handleUploadEpdFile = async () => {
    try {
      // Step 1 - Get batch upload
      const result = await getBatchUploadFormat({
        variables: {
          businessUnitId: String(getBUFromLocalStorage()),
          slug: selectedSystem?.slug || "",
        },
      });

      // Step 2 - Upload cvs
      const { data: uploadCSVData } = await uploadCsvMutation({
        variables: {
          input: {
            businessUnit: getBUFromLocalStorage(),
            csvFile: file?.file,
          },
        },
      });

      if (uploadCSVData?.uploadCsv?.errors) {
        setError(uploadCSVData?.uploadCsv?.errors.map((item) => item?.message).join("; "));
        return;
      }

      const matchResult = handleMatchColumns(
        uploadCSVData?.uploadCsv || undefined,
        result?.data?.batchUploadFormat?.columns || [],
      );
      if (matchResult.error) {
        setError(matchResult.error);
        return;
      }

      // Step 3 - import csv
      const { data: importData } = await importCsvMutation({
        variables: {
          input: {
            id: Number(uploadCSVData?.uploadCsv?.id),
            fields: matchResult.columns,
            isFirstRowColumnNames: true,
            formatSlug: result?.data?.batchUploadFormat?.slug,
          },
        },
      });

      if (importData?.importCsv?.errors) {
        setError(importData?.importCsv?.errors.map((item) => item?.message).join("; "));
        return;
      }

      if (importData?.importCsv?.taskId) {
        dispatch(setImportMenuState("expanded"));
        dispatch(setImportTaskId(importData?.importCsv?.taskId));
      }
    } catch (e) {
      setError("An unexpected error occurred. Please try again");
    }
  };

  const handleUploadXlsMutation = async (systemMutation, mutationName: string): Promise<void> => {
    const { data } = await systemMutation({
      variables: {
        input: {
          farm: getBUFromLocalStorage(),
          xlsFile: file?.file,
        },
      },
    });

    if (data?.[mutationName]?.task) {
      setTaskIdToLocalStorage(data?.[mutationName]?.task.id);
      setTaskId(data?.[mutationName]?.task.id);
    }

    if (data?.[mutationName]?.errors) {
      setError(data?.[mutationName]?.errors.map((item) => item?.message).join("; "));
    }
  };

  const handleUploadCsvMutation = async (systemMutation, mutationName: string): Promise<void> => {
    const { data } = await systemMutation({
      variables: {
        input: {
          farm: getBUFromLocalStorage(),
          csvFile: file?.file,
        },
      },
    });

    if (data?.[mutationName]?.task) {
      setTaskIdToLocalStorage(data?.[mutationName]?.task.id);
      setTaskId(data?.[mutationName]?.task.id);
    }

    if (data?.[mutationName]?.errors) {
      setError(data?.[mutationName]?.errors.map((item) => item?.message).join("; "));
    }
  };

  const handleUploadFile = (): void => {
    switch (selectedSystem?.slug) {
      case BCMS_SLUG:
        handleUploadCsvMutation(uploadBcmsFile, "bcmsImportCsv");
        eventsRequest({ name: "import-bcms" });
        break;

      case APHIS_SLUG:
        handleUploadCsvMutation(uploadAphisFile, "aphisImportCsv");
        eventsRequest({ name: "import-aphis" });
        break;

      case TRU_TEST_SLUG:
        handleUploadXlsMutation(uploadTruTestFile, "truTestImportXls");
        eventsRequest({ name: "import-trutest" });
        break;

      case FARMPLAN_SLUG:
        handleUploadXlsMutation(uploadFarmPlanFile, "farmPlanImportXls");
        eventsRequest({ name: "import-farmplan" });
        break;

      case AA_SLUG:
        handleUploadEpdFile();
        break;

      case NEOGEN_SLUG:
        handleUploadEpdFile();
        break;
    }
  };

  const acceptedFormats = [XLS_FILE_FORMAT, CSV_FILE_FORMAT];

  const history = useHistory();
  const handleClickBack = () => history.push(IMPORT);

  return (
    <Flex container>
      <Flex item itemGutter xs={12}>
        <Flex className={styles.import_weight_form} container containerJustifyContent="center">
          <Spacer baselineHeight={3} />

          <Flex item itemGutter>
            <Text smaller>
              <strong>Supported systems:</strong>
            </Text>
          </Flex>

          <Spacer baselineHeight={4} />

          {integrationSystems?.map((integrationSystem) => (
            <Flex item itemGutter key={`supported_system_${integrationSystem.id}`}>
              <SystemSelect
                logo={integrationSystem.logo as string}
                name={integrationSystem.name}
                onClick={(): void => setSelectedSystem(integrationSystem)}
                selected={integrationSystem.id === selectedSystem?.id}
              />
            </Flex>
          ))}

          {selectedSystem ? (
            <>
              <Spacer baselineHeight={4} />

              <Flex item itemGutter>
                <Text smaller>
                  <strong>Upload your {selectedSystem.name} file:</strong>
                </Text>
              </Flex>

              {selectedSystem.helpLink ? (
                <>
                  <Spacer baselineHeight={2} />

                  <TextAnchor
                    caption="Not sure how to get one? Click here for instructions"
                    href={selectedSystem.helpLink}
                    id={ELEMENTS_IDS.IMPORT_BLOCK_INSTRUCTIONS_LINK}
                    rel="noopener noreferrer"
                    target="_blank"
                  />
                </>
              ) : null}

              <Spacer baselineHeight={4} />

              <FileInput
                uppercase
                onChange={handleSetFile}
                label={file?.fileName || "Choose file..."}
                acceptedFormats={acceptedFormats}
                id="weightFileUpload"
              />

              {error ? (
                <>
                  <Spacer baselineHeight={2} />

                  <Text smaller warning className="px-2">
                    {error}
                  </Text>
                </>
              ) : null}

              {file?.fileName ? (
                <>
                  <Spacer baselineHeight={3} />

                  <Flex container>
                    <Flex item itemGutter>
                      <Button
                        id={ELEMENTS_IDS.IMPORT_BLOCK_CANCEL_BUTTON}
                        caption="Cancel"
                        colour="grey"
                        variant="hollow"
                        onClick={handleRemoveFile}
                      />
                    </Flex>

                    <Flex item itemGutter>
                      <Button
                        id={ELEMENTS_IDS.IMPORT_BLOCK_IMPORT_BUTTON}
                        caption="Import"
                        onClick={handleUploadFile}
                      />
                    </Flex>
                  </Flex>
                </>
              ) : null}

              <Spacer baselineHeight={4} />
            </>
          ) : null}
        </Flex>

        <Spacer baselineHeight={4} />

        <Flex container containerJustifyContent="space-between">
          <Button caption="Back" colour="grey" onClick={handleClickBack} variant="hollow" />
        </Flex>
      </Flex>
      <AsyncJobStatusMenuImport />
    </Flex>
  );
};
